import { Drawer, DrawerProps, Fade, SxProps, useTheme } from '@mui/material';
import { RiCloseLine } from '@remixicon/react';
import { type CSSProperties, useEffect, useState } from 'react';
import { GLOBAL_APP_BAR_HEIGHT } from 'src/modules/pageFrame/SiteHeader';
import { create } from 'zustand';
import Flex from '../atoms/Flex';
import { AgButton } from '../ui';

export enum DrawerId {
  PALLET_TAG_SELECTION,
  GENERATE_DOCUMENT,
  PALLET_TAG_CELL,
  LOT_TRACEABILITY,
  PAYMENT,
  ORDER_JACKET,
  APPLY_ADJUSTMENT,
  CONFIGURE_REPORT,
  INVITE_MEMBER,
  AI_CHAT,
  ADD_PRODUCT,
  ORDER_HISTORY,
  INVENTORY_SEARCH,
}

type SideDrawerStore = {
  openId: DrawerId | string | null;
  isHidden: boolean;
  payload: unknown;
  openDrawer: (id: DrawerId | string, payload?: unknown) => void;
  closeDrawer: () => void;
  hideDrawer: () => void;
  revealDrawer: () => void;
};

export const useSideDrawerState = create<SideDrawerStore>((set) => ({
  isHidden: false,
  openId: null,
  payload: undefined,
  openDrawer: (id, payload?) => set({ openId: id, isHidden: false, payload }),
  closeDrawer: () => set({ openId: null, isHidden: false, payload: undefined }),
  hideDrawer: () => set({ isHidden: true }),
  revealDrawer: () => set({ isHidden: false }),
}));

export const useIsSideDrawerOpen = () => useSideDrawerState((state) => state.openId !== null);

export const PALLET_TAG_DRAWER_IDS = [DrawerId.PALLET_TAG_SELECTION, DrawerId.PALLET_TAG_CELL] as const;

// True when the open drawer is a pallet-tag drawer (matches the `${id}-${rowId}` cell variant too).
export const isPalletTagDrawerOpen = (openId: DrawerId | string | null): boolean =>
  openId !== null &&
  PALLET_TAG_DRAWER_IDS.some((id) => openId === id || (typeof openId === 'string' && openId.startsWith(`${id}-`)));

export const useSideDrawer = () => ({
  openDrawer: useSideDrawerState((state) => state.openDrawer),
  closeDrawer: useSideDrawerState((state) => state.closeDrawer),
  hideDrawer: useSideDrawerState((state) => state.hideDrawer),
  revealDrawer: useSideDrawerState((state) => state.revealDrawer),
  payload: useSideDrawerState((state) => state.payload),
});

type SideDrawerProps = DrawerProps & {
  drawerId: DrawerId | string;
  paperSx?: SxProps;
  onClose?: () => void;
  /** Prevents the drawer from closing when clicking the backdrop */
  disableBackdropClick?: boolean;
};

export function SideDrawer(props: SideDrawerProps) {
  const { drawerId, children, paperSx, onClose, disableBackdropClick, key: _, ...drawerProps } = props;
  const isHidden = useSideDrawerState((state) => state.isHidden);
  const closeDrawer = useSideDrawerState((state) => state.closeDrawer);
  const open = useSideDrawerState((state) => state.openId === drawerId);
  const [hideEffect, setHideEffect] = useState<CSSProperties | null>(null);

  const handleClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    closeDrawer();
    onClose?.();
  };

  const theme = useTheme();

  // Prevent the open and hide animations from conflicting. TODO: replace MUI's drawer animations with our own so collapsing can be implemented as designed.
  useEffect(() => {
    if (hideEffect && !isHidden) {
      setHideEffect({ transform: 'translateX(0)', transition: 'transform 0.3s ease-in-out' });

      setTimeout(() => {
        setHideEffect(null);
      }, 300);
    }

    if (isHidden && !hideEffect) {
      setHideEffect({ transform: 'translateX(90%)', transition: 'transform 0.3s ease-in-out' });
    }
  }, [isHidden, hideEffect]);

  return (
    <Drawer
      variant="temporary" // Keep the temporary variant
      anchor="right"
      open={open}
      onClose={handleClose}
      {...drawerProps}
      slotProps={{
        paper: {
          ...(drawerProps?.slotProps?.paper ?? {}),
          sx: {
            top: `${GLOBAL_APP_BAR_HEIGHT}px`,
            py: '12px',
            px: '24px',
            pb: '36px',
            maxWidth: '95vw',
            overflowY: 'auto',
            ...(paperSx ?? (drawerProps?.slotProps?.paper as { sx?: object })?.sx ?? {}),
            position: 'absolute',
            height: `calc(100% - ${GLOBAL_APP_BAR_HEIGHT}px)`,
          },
        },
        transition: {
          ...((drawerProps?.slotProps as { transition?: object })?.transition ?? {}),
          style: {
            ...(hideEffect ?? {}),
            ...((drawerProps?.slotProps as { transition?: { style?: object } })?.transition?.style ?? {}),
          },
        },
      }}
    >
      <Flex col height="100%">
        {children}
      </Flex>

      {/* This shade serves to allow dialog models to hide their shade, so that there aren't multiple conflicting shades,
        but keeps the side drawer shaded. */}
      <Fade in={isHidden} timeout={300}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: theme.palette.action.disabledBackground,
            opacity: isHidden ? 1 : 0,
            transition: `opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)`,
            zIndex: 1,
          }}
        />
      </Fade>
    </Drawer>
  );
}

type SideDrawerHeaderProps = { children?: React.ReactNode; title?: string; buttonSx?: SxProps; onClose?: () => void };

export function SideDrawerHeader({ title, children, buttonSx, onClose }: SideDrawerHeaderProps) {
  const closeDrawer = useSideDrawerState((state) => state.closeDrawer);

  const handleClose = () => {
    closeDrawer();
    onClose?.();
  };

  return (
    <Flex alignItems="flex-start" mb={1.5} gap={1}>
      <div className="flex flex-col">
        {title && <b>{title}</b>}
        {children}
      </div>
      <Flex flexGrow={1} />
      <AgButton variant="ghost" onPress={handleClose} className="gap-1 shrink-0">
        <RiCloseLine size={18} />
        <p className="leading-none">Close</p>
      </AgButton>
    </Flex>
  );
}
